import { useThree } from "@react-three/fiber";
import { useCallback, useEffect, useRef } from "react";
import * as THREE from "three";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import type { BuildInput } from "../../engines/types";
import { OpeningDragController } from "./OpeningDragController";
import { PartitionDragController } from "./PartitionDragController";
import type { OrbitControlsRef } from "./SceneControls";

interface DragControllerProps {
	buildInput: BuildInput;
	/**
	 * The rotated sauna rig. Pick planes and ghosts are parented to it so
	 * placement stays correct at a rotated orientation.
	 */
	rigRef?: React.RefObject<THREE.Object3D | null>;
	isDraggingWindow: { widthMm: number; heightMm: number } | null;
	onWindowPlaced?: (opening: WallOpening) => void;
	onOpeningMoved?: (opening: WallOpening) => void;
	onPartitionSplitChange?: (partitionSplit: number) => void;
	onDragCancelled?: () => void;
	controlsRef: React.RefObject<OrbitControlsRef | null>;
}

/**
 * R3F bridge for WindowDragController.
 * Accesses scene, camera, and renderer via useThree() and passes them
 * to the existing imperative controller.
 */
export function DragController({
	buildInput,
	rigRef,
	isDraggingWindow,
	onWindowPlaced,
	onOpeningMoved,
	onPartitionSplitChange,
	onDragCancelled,
	controlsRef,
}: DragControllerProps) {
	const scene = useThree((s) => s.scene);
	const camera = useThree((s) => s.camera);
	const gl = useThree((s) => s.gl);
	const invalidate = useThree((s) => s.invalidate);

	const dragControllerRef = useRef<
		OpeningDragController | PartitionDragController | null
	>(null);
	const hoverHandleRef = useRef<THREE.Object3D | null>(null);
	const activeHandleRef = useRef<THREE.Object3D | null>(null);

	const setControlsEnabled = useCallback(
		(enabled: boolean) => {
			if (controlsRef.current) {
				controlsRef.current.enabled = enabled;
			}
		},
		[controlsRef],
	);

	const setHandleHighlight = (
		object: THREE.Object3D | null,
		active: boolean,
	) => {
		if (
			!(object instanceof THREE.Mesh) ||
			!(object.material instanceof THREE.MeshBasicMaterial)
		) {
			return;
		}
		object.material.opacity = active ? 0.28 : 0.12;
	};

	useEffect(() => {
		if (dragControllerRef.current) {
			dragControllerRef.current.dispose();
			dragControllerRef.current = null;
		}

		if (!isDraggingWindow || !onWindowPlaced) return;

		// Keep orbit enabled during place mode — the user needs to be
		// able to rotate the model to see the wall they're aiming at.
		// OpeningDragController gates its ghost updates on
		// `event.buttons === 0` and only commits on a true click, so
		// orbit-drag gestures pass through to OrbitControls untouched.

		const controller = new OpeningDragController({
			scene,
			parent: rigRef?.current ?? undefined,
			camera: camera as THREE.PerspectiveCamera,
			renderer: gl,
			dimensions: buildInput.dimensions,
			existingOpenings: buildInput.openings,
			overrides: buildInput.overrides,
			partitionSplit: buildInput.partitionSplit,
 hasRelaxRoom: buildInput.hasRelaxRoom ?? buildInput.partitionSplit !== undefined,
 saunaPosition: buildInput.saunaPosition,
 roofConfig: buildInput.roofConfig,
 wallHeightM: buildInput.wallHeightM,
			onCommit: (opening) => {
				onWindowPlaced(opening);
				setControlsEnabled(true);
			},
			onDragUpdate: () => {
				invalidate();
			},
		});

		controller.startPlaceDrag(
			isDraggingWindow.widthMm,
			isDraggingWindow.heightMm,
		);
		dragControllerRef.current = controller;

		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				controller.cancelDrag();
				setControlsEnabled(true);
				onDragCancelled?.();
			}
		};
		window.addEventListener("keydown", handleEscape);

		return () => {
			window.removeEventListener("keydown", handleEscape);
			controller.dispose();
			dragControllerRef.current = null;
			setControlsEnabled(true);
		};
	}, [
		isDraggingWindow,
		buildInput.dimensions,
		buildInput.wallHeightM,
		buildInput.openings,
		buildInput.overrides,
		buildInput.partitionSplit,
		onWindowPlaced,
		onDragCancelled,
		rigRef,
		scene,
		camera,
		gl,
		invalidate,
		controlsRef,
		setControlsEnabled,
	]);

	useEffect(() => {
		if (isDraggingWindow) {
			return undefined;
		}

		const domElement = gl.domElement;
		const raycaster = new THREE.Raycaster();
		const mouse = new THREE.Vector2();

		const getInteractionTargets = () => {
			const targets: THREE.Object3D[] = [];
			scene.traverse((child) => {
				if (child.userData?.interactionKind) {
					targets.push(child);
				}
			});
			return targets;
		};

		const pickHandle = (event: PointerEvent) => {
			const rect = domElement.getBoundingClientRect();
			mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
			mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
			raycaster.setFromCamera(mouse, camera as THREE.PerspectiveCamera);
			return (
				raycaster.intersectObjects(getInteractionTargets(), false)[0]?.object ??
				null
			);
		};

		const cleanupActiveDrag = () => {
			dragControllerRef.current?.dispose();
			dragControllerRef.current = null;
			if (activeHandleRef.current) {
				activeHandleRef.current.visible = true;
				activeHandleRef.current = null;
			}
			setControlsEnabled(true);
			invalidate();
		};

		const handlePointerMove = (event: PointerEvent) => {
			if (dragControllerRef.current) return;

			const handle = pickHandle(event);
			if (hoverHandleRef.current !== handle) {
				setHandleHighlight(hoverHandleRef.current, false);
				hoverHandleRef.current = handle;
				setHandleHighlight(handle, true);
			}

			domElement.style.cursor = handle?.userData?.cursor ?? "";
			invalidate();
		};

		const handlePointerDown = (event: PointerEvent) => {
			if (dragControllerRef.current || isDraggingWindow) {
				return;
			}

			const handle = pickHandle(event);
			if (!handle) return;

			event.preventDefault();
			event.stopPropagation();

			setControlsEnabled(false);
			activeHandleRef.current = handle;
			activeHandleRef.current.visible = false;

			if (
				handle.userData?.interactionKind === "partition" &&
				onPartitionSplitChange
			) {
				const controller = new PartitionDragController({
					scene,
					parent: rigRef?.current ?? undefined,
					camera: camera as THREE.PerspectiveCamera,
					renderer: gl,
					dimensions: buildInput.dimensions,
					partitionSplit: buildInput.partitionSplit ?? 0.5,
					heaterType: buildInput.thermalInputs?.heaterType,
					heaterModelId: buildInput.thermalInputs?.heaterModelId,
					wallHeightM: buildInput.wallHeightM,
					onCommit: (partitionSplit) => {
						onPartitionSplitChange(partitionSplit);
						cleanupActiveDrag();
					},
					onCancel: cleanupActiveDrag,
					onDragUpdate: () => invalidate(),
				});
				controller.startDrag();
				dragControllerRef.current = controller;
				return;
			}

			if (handle.userData?.interactionKind === "opening" && onOpeningMoved) {
				const opening = buildInput.openings.find(
					(candidate) => candidate.id === handle.userData.openingId,
				);
				if (!opening) {
					cleanupActiveDrag();
					return;
				}

				const controller = new OpeningDragController({
					scene,
					parent: rigRef?.current ?? undefined,
					camera: camera as THREE.PerspectiveCamera,
					renderer: gl,
					dimensions: buildInput.dimensions,
					existingOpenings: buildInput.openings,
					overrides: buildInput.overrides,
					partitionSplit: buildInput.partitionSplit,
 hasRelaxRoom: buildInput.hasRelaxRoom ?? buildInput.partitionSplit !== undefined,
 saunaPosition: buildInput.saunaPosition,
 roofConfig: buildInput.roofConfig,
 wallHeightM: buildInput.wallHeightM,
					onCommit: (nextOpening) => {
						onOpeningMoved(nextOpening);
						cleanupActiveDrag();
					},
					onCancel: cleanupActiveDrag,
					onDragUpdate: () => invalidate(),
				});
				controller.startMoveDrag(opening);
				dragControllerRef.current = controller;
				return;
			}

			cleanupActiveDrag();
		};

		domElement.addEventListener("pointermove", handlePointerMove);
		domElement.addEventListener("pointerdown", handlePointerDown);

		return () => {
			domElement.removeEventListener("pointermove", handlePointerMove);
			domElement.removeEventListener("pointerdown", handlePointerDown);
			domElement.style.cursor = "";
			setHandleHighlight(hoverHandleRef.current, false);
			hoverHandleRef.current = null;
			// Safety: re-enable controls in case effect cleanup happens mid-drag
			setControlsEnabled(true);
		};
	}, [
		buildInput.dimensions,
		buildInput.openings,
		buildInput.overrides,
		buildInput.partitionSplit,
		buildInput.thermalInputs?.heaterType,
		buildInput.thermalInputs?.heaterModelId,
		buildInput.wallHeightM,
		camera,
		gl,
		invalidate,
		isDraggingWindow,
		onOpeningMoved,
		onPartitionSplitChange,
		rigRef,
		scene,
		setControlsEnabled,
	]);

	return null;
}
